import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface HistoryFilterValue {
  model: string;
  period: 'all' | 'today' | '7d' | '30d';
  order: 'desc' | 'asc';
}

export interface HistoryModelOption {
  id: string;
  label: string;
  count: number;
}

interface HistoryFilterSheetProps {
  isOpen: boolean;
  value: HistoryFilterValue;
  modelOptions: HistoryModelOption[];
  loading?: boolean;
  onClose: () => void;
  onApply: (value: HistoryFilterValue) => void;
}

const DEFAULT_FILTERS: HistoryFilterValue = {
  model: 'all',
  period: 'all',
  order: 'desc',
};

export default function HistoryFilterSheet({
  isOpen,
  value,
  modelOptions,
  loading = false,
  onClose,
  onApply,
}: HistoryFilterSheetProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<HistoryFilterValue>(value);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(value);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose, value]);

  if (!isOpen) return null;

  const optionClass = (selected: boolean) => (
    `min-h-11 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
      selected
        ? 'border-purple-400 bg-purple-50 text-purple-600'
        : 'border-gray-100 bg-gray-50 text-gray-600 active:bg-gray-100'
    }`
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" data-testid="history-filter-sheet">
      <button
        type="button"
        aria-label={t('profile_filterClose', '关闭筛选')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-filter-title"
        className="relative flex max-h-[82dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white animate-slide-up"
      >
        <div className="flex shrink-0 items-center justify-between px-5 pb-3 pt-5">
          <h2 id="history-filter-title" className="text-lg font-bold text-gray-800">
            {t('profile_filterTitle', '筛选生成记录')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t('profile_filterClose', '关闭筛选')}
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 pb-5 ios-scroll">
          <section aria-labelledby="history-filter-model-title">
            <h3 id="history-filter-model-title" className="mb-3 text-sm font-bold text-gray-700">
              {t('profile_filterModel', '模型')}
            </h3>
            {loading ? (
              <div className="grid grid-cols-2 gap-2" data-testid="history-filter-model-loading">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="h-11 animate-pulse rounded-xl bg-gray-100" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  aria-pressed={draft.model === 'all'}
                  onClick={() => setDraft((current) => ({ ...current, model: 'all' }))}
                  className={optionClass(draft.model === 'all')}
                >
                  {t('profile_filterAllModels', '全部模型')}
                </button>
                {modelOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={draft.model === option.id}
                    onClick={() => setDraft((current) => ({ ...current, model: option.id }))}
                    className={`${optionClass(draft.model === option.id)} flex items-center justify-between gap-2`}
                  >
                    <span className="truncate">{option.label}</span>
                    <span className="shrink-0 text-[10px] font-medium text-gray-400">{option.count}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section aria-labelledby="history-filter-period-title">
            <h3 id="history-filter-period-title" className="mb-3 text-sm font-bold text-gray-700">
              {t('profile_filterPeriod', '时间')}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['all', t('profile_filterAllTime', '全部时间')],
                ['today', t('profile_filterToday', '今天')],
                ['7d', t('profile_filter7Days', '近 7 天')],
                ['30d', t('profile_filter30Days', '近 30 天')],
              ] as const).map(([period, label]) => (
                <button
                  key={period}
                  type="button"
                  aria-pressed={draft.period === period}
                  onClick={() => setDraft((current) => ({ ...current, period }))}
                  className={optionClass(draft.period === period)}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section aria-labelledby="history-filter-order-title">
            <h3 id="history-filter-order-title" className="mb-3 text-sm font-bold text-gray-700">
              {t('profile_filterOrder', '排序')}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['desc', t('profile_filterNewest', '最新优先')],
                ['asc', t('profile_filterOldest', '最早优先')],
              ] as const).map(([order, label]) => (
                <button
                  key={order}
                  type="button"
                  aria-pressed={draft.order === order}
                  onClick={() => setDraft((current) => ({ ...current, order }))}
                  className={optionClass(draft.order === order)}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        </div>

        <div
          className="grid shrink-0 grid-cols-[0.8fr_1.6fr] gap-3 border-t border-gray-100 bg-white px-5 pt-4"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={() => setDraft(DEFAULT_FILTERS)}
            className="h-12 rounded-2xl bg-gray-100 text-sm font-semibold text-gray-600 active:bg-gray-200"
          >
            {t('profile_filterReset', '重置')}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => onApply(draft)}
            className="h-12 rounded-2xl bg-gradient-to-r from-[#FF6B9D] to-[#A855F7] text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('profile_filterViewResults', '查看结果')}
          </button>
        </div>
      </div>
    </div>
  );
}
